import fetchSingleCustomer from "api/companies/fetchSingleCustomer";
import CustomMenu from "components/CustomizedMui/CustomMenu";
import fetchApplicants from "layouts/pages/applicants/actions/fetchApplicants";
import fetchSingleJob from "layouts/pages/companyjobs/actions/fetchSingleJob";
import { BulkMessagingModal } from "layouts/pages/events/components/BulkMessagingModal";
import { useMemo, useState } from "react";
import { useQuery } from "react-query";

const useInterviewBulkMessageMenu = () => {
  const [bulkMessageModalCurrentMode, setBulkMessageModalCurrentMode] = useState(null);
  const [bulkMessageModalCurrentEvent, setBulkMessageModalCurrentEvent] = useState(null);

  const [anchorEl, setAnchorEl] = useState(null);

  const allOptionsApplicants = {
    fetchAll: true,
    filters: {
      _id: [
        ...(bulkMessageModalCurrentEvent?.applicants ?? []),
        ...(bulkMessageModalCurrentEvent?.attendees ?? []),
      ]
        .flatMap((app) =>
          app.applicantId || app.id || app._id ? [app.applicantId || app.id || app._id] : []
        )
        ?.join(";"),
    },
  };

  const {
    data: applicants,
    isLoading: isLoadingApplicants,
    isFetching: isFetchingApplicants,
    refetch: refetchApplicants,
  } = useQuery(
    ["applicants", allOptionsApplicants],
    () => {
      return fetchApplicants(allOptionsApplicants);
    },
    {
      enabled:
        !!bulkMessageModalCurrentEvent &&
        !!bulkMessageModalCurrentMode &&
        !![
          ...(bulkMessageModalCurrentEvent?.applicants ?? []),
          ...(bulkMessageModalCurrentEvent?.attendees ?? []),
        ].length,
    }
  );

  const {
    data: interviewJob,
    isLoading: jobIsLoading,
    isFetching: isFetchingJob,
  } = useQuery(
    ["interviewJob", bulkMessageModalCurrentEvent?.jobSlug],
    () => {
      return fetchSingleJob(bulkMessageModalCurrentEvent?.jobSlug);
    },
    {
      enabled: !!bulkMessageModalCurrentEvent?.jobSlug,
    }
  );

  const {
    data: interviewCustomer,
    isLoading: customerIsLoading,
    isFetching: isFetchingCustomer,
  } = useQuery(
    ["interviewCustomer", interviewJob?.data?.job?.companySlug],
    () => {
      return fetchSingleCustomer({ slug: interviewJob?.data?.job?.companySlug });
    },
    {
      enabled: !!interviewJob?.data?.job?.companySlug,
    }
  );

  const onOpenMessageMenu = (e, event) => {
    setAnchorEl(e.currentTarget);
    setBulkMessageModalCurrentEvent(event);
  };

  const [applicantInfoList, applicantAttachments] = useMemo(() => {
    if (!applicants?.data) return [[], []];
    const applicantsIndexedById = {};
    const allAttachments = [];
    (bulkMessageModalCurrentEvent?.applicants ?? []).forEach((appl) => {
      const id = appl.id || appl._id;
      applicantsIndexedById[id] = appl;
    });
    const result = applicants.data?.filter((appl) => {
      const shouldInclude = !!applicantsIndexedById[appl._id];
      if (shouldInclude) {
        allAttachments.push(
          ...(appl.attachments?.map((att) => ({
            ...att,
            filename: att.filename || att.name,
            group: `${appl.firstName} ${appl.lastName}`,
            entity: "Applicant",
            applicantId: appl._id,
          })) ?? [])
        );
      }
      return shouldInclude;
    });
    return [result, allAttachments];
  }, [applicants?.data, bulkMessageModalCurrentEvent?.applicants]);

  const teamMemberInfoList = useMemo(() => {
    if (!applicants?.data) return [];
    const applicantsIndexedById = {};
    (bulkMessageModalCurrentEvent?.attendees ?? []).forEach((usr) => {
      const id = usr.status === "Team Member" ? usr._id : usr.applicantId;
      applicantsIndexedById[id] = usr;
    });
    return applicants.data?.filter((appl) => !!applicantsIndexedById[appl._id]);
  }, [applicants?.data, bulkMessageModalCurrentEvent?.attendees]);

  const jobAttachments = useMemo(() => {
    const newAtt = [];
    if (interviewCustomer?.data?.attachments?.length) {
      newAtt.push(
        ...interviewCustomer.data.attachments.map((att) => ({
          entity: "Customer",
          group: "Customer",
          filename: att.filename,
          companySlug: interviewCustomer.data.slug,
        }))
      );
    }
    if (interviewJob?.data?.job?.attachments?.length) {
      newAtt.push(
        ...interviewJob.data.job.attachments.map((att) => ({
          entity: "Job",
          group: "Job",
          filename: att.filename,
          jobSlug: bulkMessageModalCurrentEvent?.jobSlug,
        }))
      );
    }
    if (bulkMessageModalCurrentEvent?.attachments?.length) {
      newAtt.push(
        ...bulkMessageModalCurrentEvent.attachments.map((att) => ({
          entity: "Event",
          group: "Event",
          filename: att.filename,
          venueSlug: bulkMessageModalCurrentEvent.companySlug,
          eventUrl: bulkMessageModalCurrentEvent.eventUrl,
        }))
      );
    }
    return newAtt;
  }, [
    bulkMessageModalCurrentEvent?.attachments,
    bulkMessageModalCurrentEvent?.companySlug,
    bulkMessageModalCurrentEvent?.eventUrl,
    bulkMessageModalCurrentEvent?.jobSlug,
    interviewCustomer?.data?.attachments,
    interviewCustomer?.data?.slug,
    interviewJob?.data?.job?.attachments,
  ]);

  const bulkMessageModal = (
    <>
      {!!bulkMessageModalCurrentMode && !!bulkMessageModalCurrentEvent && (
        <BulkMessagingModal
          applicants={
            bulkMessageModalCurrentMode === "applicants" ? applicantInfoList : teamMemberInfoList
          }
          isOpen={!!bulkMessageModalCurrentMode}
          toggleModal={() => {
            setBulkMessageModalCurrentMode(null);
            setBulkMessageModalCurrentEvent(null);
          }}
          currentEvent={bulkMessageModalCurrentEvent}
          jobId={interviewJob?.data?.job?._id}
          messageContext="Event"
          attachmentContext={bulkMessageModalCurrentMode === "applicants" ? "Event" : "Custom"}
          customAttachments={
            bulkMessageModalCurrentMode === "applicants" ? jobAttachments : applicantAttachments
          }
          customTemplates={
            bulkMessageModalCurrentEvent?.description
              ? [
                  {
                    name: "<< Interview Description >>",
                    subject: "Interview Invitation",
                    body: `<p>${bulkMessageModalCurrentEvent?.description}</p>`,
                  },
                ]
              : null
          }
          isLoadingExtras={isFetchingApplicants || isFetchingJob || isFetchingCustomer}
          disableAddAttachment={!bulkMessageModalCurrentEvent?._id}
        />
      )}
    </>
  );

  const menuOptions = useMemo(
    () => [
      {
        text: "Applicants",
        handleClick: (e) => {
          setBulkMessageModalCurrentMode("applicants");
        },
      },
      {
        text: "Team Members",
        handleClick: (e) => {
          setBulkMessageModalCurrentMode("attendees");
        },
      },
    ],
    []
  );

  const customMenu = (
    <CustomMenu
      anchorEl={anchorEl}
      handleClose={(e) => {
        e?.stopPropagation();
        setAnchorEl(null);
      }}
      options={menuOptions}
      iconMargin="5px"
    />
  );

  return { onOpenMessageMenu, bulkMessageModal, customMenu };
};

export default useInterviewBulkMessageMenu;
